import { UI_CONSTANTS } from "../../Constants/constants";
import { IFieldMetaData } from "../IField";
import { comparisionValues } from "../IValidationRule";
import { IProductRules } from "./IProductValidation";

const TOTE = "TOTE";

export interface IProductFieldMetaData extends IFieldMetaData {
  rules: IProductRules;
}

export interface IDimensionsMetaData {
  height: IProductFieldMetaData;
  heightUOM: IProductFieldMetaData;
  width: IProductFieldMetaData;
  widthUOM: IProductFieldMetaData;
  length: IProductFieldMetaData;
  lengthUOM: IProductFieldMetaData;
  weight: IProductFieldMetaData;
  weightUOM: IProductFieldMetaData;
  ti: IProductFieldMetaData;
  hi: IProductFieldMetaData;
}

export interface IOuterDimensionsMetaData extends IDimensionsMetaData {
  breakPackQty: IProductFieldMetaData;
}

export const ERROR_MESSAGES = {
  required: "This field is required",
  decimal: "This field only takes decimal values",
  integer: (value: string | number) =>
    `Entered value (e.g. '${value}') is not an integer.`,
  inputRange: (
    lowerLimit: number | undefined,
    upperLimit: number | undefined,
    unit: string | undefined = "",
    comparision?: comparisionValues
  ) => {
    switch (comparision) {
      case "greater": {
        return `Value must be greater than ${lowerLimit} ${unit}`;
      }
      default: {
        return `Value must be greater than or equal to ${lowerLimit} ${unit} and less than or equal to ${upperLimit} ${unit}`;
      }
    }
  },
  breakpackHeightGteqCaseHeight: "Breakpack Height must be greater than or equal to Case Height",
  breakpackLengthGteqCaseLength: "Breakpack Length must be greater than or equal to Case Length",
  breakpackWidthGteqCaseWidth: "Breakpack Width must be greater than or equal to Case Width",
  breakpackWidthLteqBreakpackLength:
    "Breakpack Width must be less than or equal to Breakpack Length",
  breakpackWeightGteqCaseWeight: "Breakpack Weight must be greater than or equal to Case Weight",
  eachLteqVendorHeight: (value: number | string, unit: string) =>
    `Each Height must be less than or equal to Vendor Height (i.e. ${value} ${unit})`,
  eachLteqVendorLength: (value: number | string, unit: string) =>
    `Each Length must be less than or equal to Vendor Length (i.e. ${value} ${unit})`,
  eachLteqVendorWidth: (value: number | string, unit: string) =>
    `Each Width must be less than or equal to Vendor Width (i.e. ${value} ${unit})`,
  eachLteqVendorWeight: (value: number | string, unit: string) =>
    `Each Weight must be less than Vendor Weight (i.e. ${value} ${unit})`,
  vendorGteqEachHeight: (value: number | string, unit: string) =>
    `Vendor Height must be greater than or equal to Each Height (i.e. ${value} ${unit})`,
  vendorGteqEachLength: (value: number | string, unit: string) =>
    `Vendor Length must be greater than or equal to Each Length (i.e. ${value} ${unit})`,
  vendorGteqEachWidth: (value: number | string, unit: string) =>
    `Vendor Width must be greater than or equal to Each Width (i.e. ${value} ${unit})`,
  vendorGteqEachWeight: (value: number | string, unit: string) =>
    `Vendor Weight must be greater than Each Weight (i.e. ${value} ${unit})`,
};

export interface IProductFormField {
  //NewLine: IProductFieldMetaData,
  sainId: IProductFieldMetaData;
  skuNumber: IProductFieldMetaData;
  catNumber: IProductFieldMetaData;
  unitOfMeasure: IProductFieldMetaData;
  description: IProductFieldMetaData;
  merchandisingUnit: IProductFieldMetaData;
  minimumLifeOnReceipt: IProductFieldMetaData;
  minimumLifeOnIssue: IProductFieldMetaData;
  productType: IProductFieldMetaData;
  familyGroup: IProductFieldMetaData;
  validation: IProductFieldMetaData;
  company: IProductFieldMetaData;
  businessUnit: IProductFieldMetaData;
  businessUnitDesc: IProductFieldMetaData;
  superCategory: IProductFieldMetaData;
  category: IProductFieldMetaData;
  categoryDesc: IProductFieldMetaData;
  subCategory: IProductFieldMetaData;
  subCategoryDesc: IProductFieldMetaData;
  superCatDesc: IProductFieldMetaData;
  segment: IProductFieldMetaData;
  segmentDesc: IProductFieldMetaData;
  oldFamilyGroup: IProductFieldMetaData;
  warehouseId: IProductFieldMetaData;
  id: IProductFieldMetaData;
  createdDate: IProductFieldMetaData;
  updatedDate: IProductFieldMetaData;
  updatedBy: IProductFieldMetaData;
  packSize: IProductFieldMetaData;
  outerLength: IProductFieldMetaData;
  outerWidth: IProductFieldMetaData;
  outerHeight: IProductFieldMetaData;
  outerWeight: IProductFieldMetaData;
  outerNetWeight: IProductFieldMetaData;
  innerLength: IProductFieldMetaData;
  innerWidth: IProductFieldMetaData;
  innerHeight: IProductFieldMetaData;
  innerWeight: IProductFieldMetaData;
  innerNetWeight: IProductFieldMetaData;
  ti: IProductFieldMetaData;
  hi: IProductFieldMetaData;
  conveyable: IProductFieldMetaData;
  twoPersonLift: IProductFieldMetaData;
  uglies: IProductFieldMetaData;
  ean: IProductFieldMetaData;
  tuc: IProductFieldMetaData;
  breakPack: IProductFieldMetaData;
  hazardous: IProductFieldMetaData;
  hazardousCode: IProductFieldMetaData;
  multiPart: IProductFieldMetaData;
  lifecycle: IProductFieldMetaData;
  activeDate: IProductFieldMetaData;
  nestedItem: IProductFieldMetaData;
  nestIncrement: IProductFieldMetaData;
  grabInBox: IProductFieldMetaData;
  measurementRequired: IProductFieldMetaData;
  skuCount: IProductFieldMetaData;
  muType: IProductFieldMetaData;
  mlor: IProductFieldMetaData;
  mloi: IProductFieldMetaData;
  error: IProductFieldMetaData;
  manual: IProductFieldMetaData;
  hasWarnings: IProductFieldMetaData;
  breakPackIndicator: IProductFieldMetaData;
  //isActive: IProductFieldMetaData;
  outerDimensions: IOuterDimensionsMetaData;
  innerDimensions: IDimensionsMetaData;
  familyGroupWithDescription: IProductFieldMetaData;
  automationAdaptoSystem: IProductFieldMetaData;
  automationToteFill: IProductFieldMetaData;
  automationMinTotes: IProductFieldMetaData;
  highRisk: IProductFieldMetaData;
  strapTotes: IProductFieldMetaData;
  expiryDateRequired: IProductFieldMetaData;
  pickPack: IProductFieldMetaData;
}

export const FIELDS: IProductFormField = {
  sainId: { name: "sainId", rules: {} },
  skuNumber: { name: "skuNumber", rules: {} },
  catNumber: { name: "catNumber", rules: {} },
  unitOfMeasure: { name: "unitOfMeasure", rules: {} },
  description: { name: "description", rules: {} },
  packSize: { name: "packSize", rules: {} },
  outerLength: { name: "outerLength", rules: {} },
  outerWidth: { name: "outerWidth", rules: {} },
  outerHeight: { name: "outerHeight", rules: {} },
  outerWeight: { name: "outerWeight", rules: {} },
  outerNetWeight: { name: "outerNetWeight", rules: {} },
  innerLength: { name: "innerLength", rules: {} },
  innerWidth: { name: "innerWidth", rules: {} },
  innerHeight: { name: "innerHeight", rules: {} },
  innerWeight: { name: "innerWeight", rules: {} },
  innerNetWeight: { name: "innerNetWeight", rules: {} },
  ti: {
    name: "ti",
    rules: {},
  },
  hi: {
    name: "hi",
    rules: {},
  },
  merchandisingUnit: { name: "merchandisingUnit", rules: {} },
  conveyable: { name: "conveyable", rules: {} },
  twoPersonLift: { name: "twoPersonLift", rules: {} },
  uglies: { name: "uglies", rules: {} },
  ean: { name: "ean", rules: {} },
  tuc: { name: "tuc", rules: {} },
  minimumLifeOnReceipt: { name: "minimumLifeOnReceipt", rules: {} },
  minimumLifeOnIssue: { name: "minimumLifeOnIssue", rules: {} },
  breakPack: { name: "breakPack", rules: {} },
  hazardous: { name: "hazardous", rules: {} },
  hazardousCode: { name: "hazardousCode", rules: {} },
  multiPart: { name: "multiPart", rules: {} },
  productType: { name: "productType", rules: {} },
  familyGroup: { name: "familyGroup", rules: {} },
  lifecycle: { name: "lifecycle", rules: {} },
  activeDate: { name: "activeDate", rules: {} },
  nestedItem: { name: "nestedItem", rules: {} },
  nestIncrement: {
    name: "nestIncrement",
    rules: {
      required: {},
      decimal: {},
      inputRange: { lowerLimit: 0, comparision: "greater" },
    },
  },
  grabInBox: { name: "grabInBox", rules: {} },
  measurementRequired: { name: "measurementRequired", rules: {} },
  validation: { name: "validation", rules: {} },
  company: { name: "company", rules: {} },
  businessUnit: { name: "businessUnit", rules: {} },
  businessUnitDesc: { name: "businessUnitDesc", rules: {} },
  superCategory: { name: "superCategory", rules: {} },
  category: { name: "category", rules: {} },
  categoryDesc: { name: "categoryDesc", rules: {} },
  subCategory: { name: "subCategory", rules: {} },
  subCategoryDesc: { name: "subCategoryDesc", rules: {} },
  superCatDesc: { name: "superCatDesc", rules: {} },
  segment: { name: "segment", rules: {} },
  segmentDesc: { name: "segmentDesc", rules: {} },
  skuCount: { name: "skuCount", rules: {} },
  muType:   { name: "muType", rules: {} },
  mlor: { name: "mlor", rules: {} },
  mloi: { name: "mloi", rules: {} },
  error: { name: "error", rules: {} },
  oldFamilyGroup: { name: "oldFamilyGroup", rules: {} },
  warehouseId: { name: "warehouseId", rules: {} },
  id: { name: "id", rules: {} },
  createdDate: { name: "createdDate", rules: {} },
  updatedDate: { name: "updatedDate", rules: {} },
  updatedBy: { name: "updatedBy", rules: {} },
  manual: { name: "manual", rules: {} },
  hasWarnings: { name: "hasWarnings", rules: {} },
  breakPackIndicator: { name: "breakPackIndicator", rules: {} },
  //isActive: { name: "isActive", rules: {} },
  outerDimensions: {
    height: {
      name: "outerDimensions_height",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        decimal: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimitCM: 1,
          upperLimitCM: 1000,
          lowerLimitM: 0.1,
          upperLimitM: 10,
        },
        customSainsburys: {},
      },
    },
    heightUOM: {
      name: "outerDimensions_heightUOM",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
      },
    },
    width: {
      name: "outerDimensions_width",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        decimal: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimitCM: 1,
          upperLimitCM: 1000,
          lowerLimitM: 0.1,
          upperLimitM: 10,
        },
        customSainsburys: {},
      },
    },
    widthUOM: {
      name: "outerDimensions_widthUOM",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
      },
    },
    length: {
      name: "outerDimensions_length",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        decimal: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimitCM: 1,
          upperLimitCM: 1000,
          lowerLimitM: 0.1,
          upperLimitM: 10,
        },
        customSainsburys: {},
      },
    },
    lengthUOM: {
      name: "outerDimensions_lengthUOM",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
      },
    },
    weight: {
      name: "outerDimensions_weight",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        decimal: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimitG: 1,
          upperLimitG: 2000000,
          lowerLimitKG: 0.001,
          upperLimitKG: 2000,
        },
        customSainsburys: {},
      },
    },
    weightUOM: {
      name: "outerDimensions_weightUOM",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
      },
    },
    ti: {
      name: "outerDimensions_ti",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        integer: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimit: 1,
          upperLimit: 999999,
        },
      },
    },
    hi: {
      name: "outerDimensions_hi",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        integer: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimit: 1,
          upperLimit: 999999,
        },
      },
    },
    breakPackQty: {
      name: "outerDimensions_breakPackQty",
      rules: {
        required: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        integer: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
        },
        inputRange: {
          validateOn: (value, formValues) => {
            return formValues.alias !== UI_CONSTANTS.ARGOS;
          },
          lowerLimit: 2,
          upperLimit: 1000,
        },
      },
    },
  },
  innerDimensions: {
    height: {
      name: "innerDimensions_height",
      rules: {
        required: {},
        decimal: {},
        inputRange: {
          lowerLimitCM: 1,
          upperLimitCM: 1000,
          lowerLimitM: 0.1,
          upperLimitM: 10,
        },
        customArgos: {},
      },
    },
    heightUOM: { name: "innerDimensions_heightUOM", rules: { required: {} } },
    width: {
      name: "innerDimensions_width",
      rules: {
        required: {},
        decimal: {},
        inputRange: {
          lowerLimitCM: 1,
          upperLimitCM: 1000,
          lowerLimitM: 0.1,
          upperLimitM: 10,
        },
        customArgos: {},
      },
    },
    widthUOM: { name: "innerDimensions_widthUOM", rules: { required: {} } },
    length: {
      name: "innerDimensions_length",
      rules: {
        required: {},
        decimal: {},
        inputRange: {
          lowerLimitCM: 1,
          upperLimitCM: 1000,
          lowerLimitM: 0.1,
          upperLimitM: 10,
        },
        customArgos: {},
      },
    },
    lengthUOM: { name: "innerDimensions_lengthUOM", rules: { required: {} } },
    weight: {
      name: "innerDimensions_weight",
      rules: {
        required: {},
        decimal: {},
        inputRange: {
          lowerLimitG: 1,
          upperLimitG: 2000000,
          lowerLimitKG: 0.001,
          upperLimitKG: 2000,
        },
        customArgos: {},
      },
    },
    weightUOM: { name: "innerDimensions_weightUOM", rules: { required: {} } },
    ti: {
      name: "innerDimensions_ti",
      rules: {
        required: {},
        integer: {},
        inputRange: { lowerLimit: 1, upperLimit: 999999 },
      },
    },
    hi: {
      name: "innerDimensions_hi",
      rules: {
        required: {},
        integer: {},
        inputRange: { lowerLimit: 1, upperLimit: 999999 },
      },
    },
  },
  familyGroupWithDescription: { name: "familyGroupWithDescription", rules: {} },
  automationAdaptoSystem: { name: "automationAdaptoSystem", rules: {} },
  automationToteFill: {
    name: "automationToteFill",
    rules: {
      required: {
        validateOn: (value, formValues) => {
          return formValues.automationAdaptoSystem === TOTE;
        },
      },
      integer: {},
    },
  },
  automationMinTotes: {
    name: "automationMinTotes",
    rules: {
      required: {
        validateOn: (value, formValues) => {
          return formValues.automationAdaptoSystem === TOTE;
        },
      },
      integer: {},
    },
  },
  highRisk: { name: "highRisk", rules: {} },
  strapTotes: { name: "strapTotes", rules: {} },
  expiryDateRequired: { name: "expiryDateRequired", rules: {} },
  pickPack: { name: "pickPack", rules: {} },
};

export enum FormInputLevels {
  NewLine = "New Product",
  SainId = "Item Number",
  SkuNumber = "Sku Number",
  CatNumber = "Cat Number",
  UnitOfMeasure = "Unit Of Measure",
  Description = "Description",
  PackSize = "UOM",
  OuterLength = "Length",
  OuterWidth = "Width",
  OuterHeight = "Height",
  OuterWeight = "Weight",
  OuterLengthUOM = "Length UOM",
  OuterWidthUOM = "Width UOM",
  OuterHeightUOM = "Height UOM",
  OuterWeightUOM = "Weight UOM",
  OuterNetWeight = "Net Weight",
  OuterTi = "Ti",
  OuterHi = "Hi",
  OuterQty = "Quantity",
  InnerLength = "Length",
  InnerWidth = "Width",
  InnerHeight = "Height",
  InnerWeight = "Weight",
  InnerLengthUOM = "Length UOM",
  InnerWidthUOM = "Width UOM",
  InnerHeightUOM = "Height UOM",
  InnerWeightUOM = "Weight UOM",
  InnerNetWeight = "Net Weight",
  InnerTi = "Ti",
  InnerHi = "Hi",
  MerchandisingUnit = "Merchandising Unit",
  Conveyable = "Conveyable",
  TwoPersonLift = "Two Person Lift",
  Uglies = "Uglies",
  EAN = "EAN",
  TUC = "TUC",
  MinimumLifeOnReceipt = "Minimum Life On Receipt",
  MinimumLifeOnIssue = "Minimum Life On Issue",
  BreakPack = "Break Pack",
  Hazardous = "Hazardous",
  HazardousCode = "Hazardous Code",
  MultiPart = "Multi Part",
  ProductType = "Product Group",
  FamilyGroup = "Family Group",
  Lifecycle = "Lifecycle",
  ActiveDate = "Active Date",
  NestedItem = "Nested",
  NestIncrement = "Nested Increment",
  GrabInBox = "Grab In Box",
  MeasurementRequired = "Measurement Required",
  Validation = "Validation",
  Company = "Company",
  BusinessUnit = "Business Unit",
  BusinessUnitDesc = "Business Unit Desc",
  SuperCategory = "Super Category",
  Category = "Category",
  CategoryDesc = "Category Desc",
  SubCategory = "Sub Category",
  SubCategoryDesc = "Sub Category Desc",
  SuperCatDesc = "Super Cat Desc",
  Segment = "Segment",
  SegmentDesc = "Segment Desc",
  SkuCount = "Sku Count",
  MUType = "MU Type",
  MLOR = "MLOR",
  MLOI = "MLOI",
  Error = "Error",
  OldFamilyGroup = "Old Family Group",
  WarehouseId = "WarehouseId",
  Id = "Id",
  CreatedDate = "Created Date",
  UpdatedDate = "Updated Date",
  UpdatedBy = "Updated By",
  FamilyGroupWithDescription = "Family Group With Description",
  DisplayItemNumber = "Display Item Number",
  AutomationAdaptoSystem = "Automation Adapto System",
  AutomationToteFill = "Automation Tote Fill",
  AutomationMinTotes = "Automation Min Totes",
  HighRisk = "High Risk",
  StrapTotes = "Strap Totes",
  ExpiryDateRequired = "Expiry Date Required",
  PickPack = "Pick Pack Indicator",
}

export const DimensionLengthUOMOptions = [
  { label: "CM (Centimetre)", value: "CM" },
];

export const DimensionWeightUOMOptions = [
  { label: "KG (Kilogram)", value: "KG" },
];

export const ProductGroupOptions = [
  { label: "AMB", value: "AMB" },
  { label: "PRD", value: "PRD" },
  { label: "CHI", value: "CHI" },
  { label: "FRZ", value: "FRZ" },
];

export const MUTypeOptions = [
  { label: "PALLET", value: "PALLET" },
  { label: "DOLLY", value: "DOLLY" },
];

export const HazardousCodeOptions = [
  { label: "CORROSIVE", value: "CORROSIVE" },
  {
    label: "HAZARDOUS TO THE ENVIRONMENT",
    value: "HAZARDOUS TO THE ENVIRONMENT",
  },
  { label: "EXPLOSIVE", value: "EXPLOSIVE" },
  { label: "GAS UNDER PRESSURE", value: "GAS UNDER PRESSURE" },
  { label: "HEALTH HAZARD", value: "HEALTH HAZARD" },
  { label: "OXIDISING", value: "OXIDISING" },
  { label: "SERIOUS HEALTH HAZARD", value: "SERIOUS HEALTH HAZARD" },
  { label: "ACUTE TOXICITY", value: "ACUTE TOXICITY" },
];

export const AutomationAdaptoSystemOptions = [
  { label: "TRAY", value: "TRAY" },
  { label: "TOTE", value: "TOTE" },
];
